export function formatCurrency(value: number | null | undefined) {
	return value !== null && value !== undefined
		? new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD"
			}).format(value)
		: "";
}

export function formatCurrencyPrecise(value: number | null | undefined) {
	return value !== null && value !== undefined
		? new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
				minimumFractionDigits: 2,
				maximumFractionDigits: 6
			}).format(value)
		: "";
}

export function formatCurrencyRound(value: number | null | undefined) {
	return value !== null && value !== undefined
		? new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value)
		: "";
}

export function formatListingPrice(price: number | null | undefined): string {
	if (price === undefined || price === null) {
		return "N/A";
	}
	if (price >= 1000000) {
		return `${(price / 1000000).toFixed(1)}M`;
	} else if (price >= 1000) {
		return `${Math.round(price / 1000)}K`;
	} else {
		return `${price.toLocaleString()}`;
	}
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function parseFlexible(value: string) {
	return new Date(DATE_ONLY.test(value) ? `${value}T00:00:00` : value);
}

export function formatDate(value: string | null | undefined) {
	return value
		? parseFlexible(value).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric"
			})
		: "N/A";
}

export function formatDateShort(value: string | null | undefined) {
	if (!value) return "—";
	const d = parseFlexible(value);
	if (isNaN(d.getTime())) return value;
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateNumeric(value: string | null | undefined) {
	if (!value) return "—";
	const d = parseFlexible(value);
	if (isNaN(d.getTime())) return value;
	return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
}

export const BUSINESS_TIME_ZONE = "America/Chicago";

function calendarDayIn(value: string | Date, timeZone: string) {
	if (typeof value === "string") {
		const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
		if (dateOnly) return { year: dateOnly[1], month: dateOnly[2], day: dateOnly[3] };
	}
	const d =
		value instanceof Date
			? value
			: new Date(value.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00"));
	if (isNaN(d.getTime())) return null;
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	}).formatToParts(d);
	const part = (type: "year" | "month" | "day") =>
		parts.find((candidate) => candidate.type === type)?.value ?? "";
	return { year: part("year"), month: part("month"), day: part("day") };
}

export function formatDateInTimeZone(value: string | Date, timeZone: string) {
	const day = calendarDayIn(value, timeZone);
	return day ? `${day.month}-${day.day}-${day.year}` : String(value);
}

export function toDateOnlyInTimeZone(value: string | Date, timeZone: string) {
	const day = calendarDayIn(value, timeZone);
	return day ? `${day.year}-${day.month}-${day.day}` : "";
}

export function formatTimestampNumeric(value: string | null | undefined, timeZone: string) {
	if (!value) return "—";
	const day = calendarDayIn(value, timeZone);
	return day ? `${day.month}/${day.day}/${day.year.slice(2)}` : value;
}

export function timestampToReadableDate(timestamp: Date, timeZone?: string): string {
	return timestamp.toLocaleString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
		...(timeZone ? { timeZone } : {})
	});
}

export function formatDateMonthDay(value: string | null | undefined) {
	if (!value) return "";
	const d = parseFlexible(value);
	if (isNaN(d.getTime())) return value;
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatDateTime(dateString: string): string {
	return new Date(dateString).toLocaleString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function parseAsUTC(dateString: string): number {
	if (/[Zz]$/.test(dateString) || /[+-]\d{2}:\d{2}$/.test(dateString)) {
		return new Date(dateString).getTime();
	}
	return new Date(dateString + "Z").getTime();
}

export function formatRelativeDate(dateString: string): string {
	const now = Date.now();
	const target = parseAsUTC(dateString);
	if (isNaN(target)) return dateString;

	const diff = now - target;
	const abs = Math.abs(diff);
	const future = diff < 0;
	const suffix = future ? "from now" : "ago";

	if (abs < MINUTE) return "just now";
	if (abs < HOUR) {
		const m = Math.floor(abs / MINUTE);
		return `${m} minute${m !== 1 ? "s" : ""} ${suffix}`;
	}
	if (abs < DAY) {
		const h = Math.floor(abs / HOUR);
		return `${h} hour${h !== 1 ? "s" : ""} ${suffix}`;
	}
	if (abs < 30 * DAY) {
		const d = Math.floor(abs / DAY);
		return `${d} day${d !== 1 ? "s" : ""} ${suffix}`;
	}
	if (abs < 365 * DAY) {
		const mo = Math.floor(abs / (30 * DAY));
		return `${mo} month${mo !== 1 ? "s" : ""} ${suffix}`;
	}
	const y = Math.floor(abs / (365 * DAY));
	return `${y} year${y !== 1 ? "s" : ""} ${suffix}`;
}

function localDateOnly(value: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (!match) return null;
	return new Date(+match[1], +match[2] - 1, +match[3]);
}

export function daysBetween(from: string, to: string): number | null {
	const a = localDateOnly(from);
	const b = localDateOnly(to);
	if (!a || !b) return null;
	return Math.round((b.getTime() - a.getTime()) / DAY);
}

export function daysFromToday(value: string, now: Date = new Date()): number | null {
	const target = localDateOnly(value);
	if (!target) return null;
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return Math.round((target.getTime() - today.getTime()) / DAY);
}

export function formatRelativeDay(value: string, now: Date = new Date()): string | null {
	const days = daysFromToday(value, now);
	if (days === null) return null;
	if (days === 0) return "Today";
	if (days === -1) return "Yesterday";
	if (days === 1) return "Tomorrow";
	if (days < 0) return `${-days} days ago`;
	if (days < 7) {
		const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
		return target.toLocaleDateString("en-US", { weekday: "long" });
	}
	return `${days} days from now`;
}

export function decimalToPercent(value: number | null | undefined) {
	return value ? `${(value * 100).toFixed(2)}%` : "N/A";
}

export function numberToPercent(value: number | null | undefined) {
	return value && value > 0 ? `${Number(value).toFixed(1)}%` : "-";
}

export function formatDSCR(value: number | null | undefined) {
	return value && value > 0 ? `${Number(value).toFixed(2)}x` : "-";
}

export function formatOrdinalDate(
	date: string | null,
	options?: { includeYear?: boolean }
): string | null {
	if (!date) return null;

	const MONTH_NAMES = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec"
	];
	const [year, month, day] = date.split("-");
	const monthIndex = parseInt(month, 10) - 1;
	const dayNum = parseInt(day, 10);

	const suffix =
		dayNum >= 11 && dayNum <= 13
			? "th"
			: dayNum % 10 === 1
				? "st"
				: dayNum % 10 === 2
					? "nd"
					: dayNum % 10 === 3
						? "rd"
						: "th";

	return options?.includeYear
		? `${MONTH_NAMES[monthIndex]} ${dayNum}${suffix}, ${year}`
		: `${MONTH_NAMES[monthIndex]} ${dayNum}${suffix}`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
	const word = count === 1 ? singular : (plural ?? `${singular}s`);
	return `${count} ${word}`;
}

export function toDateOnly(value: string | Date | null | undefined): string {
	if (!value) return "";
	const d = value instanceof Date ? value : new Date(value);
	if (isNaN(d.getTime())) return "";
	return d.toISOString().slice(0, 10);
}

export function toMonthDay(value: string | Date | null | undefined): string {
	const dateOnly = toDateOnly(value);
	return dateOnly ? dateOnly.slice(5, 10) : "";
}
